import { InjectionToken } from '@angular/core';

/**
 * What the operator did to an order that the call he is talking on may be linked for (design §3.5). The places where it is done (the shutter, the card, the change, the transfer, the cancel, the invoice, the comment) say it
 * through this token, and `CallLinkService` — provided in `main.ts` — decides. A place that gets no token (`inject(ORDER_ACTED, { optional: true })`) does nothing: the specs of the pages and the old pages work as they did.
 */
export type OrderActedKind = 'edit' | 'transfer' | 'cancel' | 'invoice' | 'comment';

/**
 * The actions that link the current call to the order — the owner's word is «any action with an order within the call». ONE line to narrow it (`B1`): take a kind out of the list and the places that say it go on saying it, to nobody.
 */
export const CALL_LINK_ACTIONS: readonly OrderActedKind[] = ['edit', 'transfer', 'cancel', 'invoice', 'comment'];

export type OrderActed =
  /** An action was done to the order: the call the workplace holds (talking, the operator's own) is linked to it. */
  | { kind: OrderActedKind; orderId: number; uniqueNumber?: string }
  /**
   * S1: the form booked while the call the form knew (E1: «the last call of the number») was NOT the one the workplace holds — a second call came before «Забронировать». The booking has no call; `callId` is the held one,
   * which the order is linked to once it is made (W3 does not take a reservation).
   */
  | { kind: 'bookedApart'; orderId: number; callId: string }
  /** The booking `orderId` became an order («Создать заказ»): an order owed to a call (`bookedApart`) is linked now. */
  | { kind: 'created'; orderId: number; uniqueNumber?: string }
  /** The operator released the booking `orderId` («Снять бронь», wherever it is pressed): not an action that links, but an order closed by the operator himself (B2′) — a waiting call opens. */
  | { kind: 'released'; orderId: number };

export interface OrderActedHook {
  acted(event: OrderActed): void;
  /** The call the workplace holds right now, whatever its phase (S1: the form of a new order compares it with the call the server offers it). */
  heldCallId?(): string | undefined;
}

export const ORDER_ACTED = new InjectionToken<OrderActedHook>('cc.orderActed');
